// @ts-nocheck
/*
 * AI Agent destination for inbound routing, shared by InboundCallsPage
 * (create) and EditInboundCallsPage (update).
 *
 * Renders the AI agent picker, then one row per completion type the selected
 * bot can emit (fetched from POST /v2/ai-agents/:agentId/completion-types).
 * Each completion type is mapped to what should happen to the live call once
 * the bot reaches it: hangup, hangup_with_media, transfer or
 * transfer_with_media. Anything left untouched is saved as hangup.
 *
 * Markup uses the same bootstrap class names as the two host pages so it
 * blends in without importing their local react-bootstrap shims.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { getApiCall } from '@/service/httpCall';
import { API_PROMPT_ACTIVE_LIST } from '@/service/url';
import { listAgents, getCompletionTypes } from '@/service/aiAgentApi';
import { getUser } from '@/utilities/setting';

export const AI_COMPLETION_EVENT_TYPES = [
  { value: 'hangup', label: 'Hangup' },
  { value: 'hangup_with_media', label: 'Hangup with Media' },
  { value: 'transfer', label: 'Transfer' },
  { value: 'transfer_with_media', label: 'Transfer with Media' },
];

export const AI_TRANSFER_TARGET_TYPES = {
  AGENT: 'agent',
  CALL_GROUP: 'call_group',
  AI: 'ai',
  IVR: 'ivr',
};

export const AI_COMPLETION_TRANSFER_TYPES = [
  { value: AI_TRANSFER_TARGET_TYPES.AGENT, label: 'Agent' },
  { value: AI_TRANSFER_TARGET_TYPES.CALL_GROUP, label: 'Call Group' },
  { value: AI_TRANSFER_TARGET_TYPES.AI, label: 'AI Agent' },
  { value: AI_TRANSFER_TARGET_TYPES.IVR, label: 'IVR' },
];

const toTransferType = (value) =>
  value === 'ai_agent' ? AI_TRANSFER_TARGET_TYPES.AI : value;

export const DEFAULT_AI_COMPLETION_EVENT = 'hangup';

const needsMedia = (eventType) =>
  eventType === 'hangup_with_media' || eventType === 'transfer_with_media';

const needsTransfer = (eventType) =>
  eventType === 'transfer' || eventType === 'transfer_with_media';

const approvedLanguageEntries = (prompt) =>
  (Array.isArray(prompt?.prompt_url) ? prompt.prompt_url : []).filter(
    (entry) => Number(entry?.status) === 1 && entry?.language,
  );

const isSelectablePrompt = (prompt) =>
  Number(prompt?.prompt_status) === 1 && approvedLanguageEntries(prompt).length > 0;

/** The AI service has been seen returning a bare array as well as a wrapper object. */
const toCompletionTypeList = (resp) => {
  const list = Array.isArray(resp)
    ? resp
    : resp?.completion_types || resp?.completion_type || resp?.completions || [];
  return (Array.isArray(list) ? list : [])
    .map((item) =>
      typeof item === 'string'
        ? { name: item, description: '' }
        : { name: item?.name || '', description: item?.description || '' },
    )
    .filter((item) => item.name);
};

/** Drop fields that do not apply to the chosen event type before sending. */
export const buildAiCompletionPayload = (rules) =>
  (Array.isArray(rules) ? rules : []).map((rule) => {
    const eventType = rule.event_type || DEFAULT_AI_COMPLETION_EVENT;
    const payload = {
      name: rule.name,
      description: rule.description || '',
      event_type: eventType,
    };
    if (needsMedia(eventType)) {
      payload.media_id = Number(rule.media_id);
      payload.media_language = rule.media_language;
    }
    if (needsTransfer(eventType)) {
      payload.transfer_type = rule.transfer_type;
      payload.transfer_id = String(rule.transfer_id);
    }
    return payload;
  });

/** @returns {string|null} first problem found, or null when the mapping is complete */
export const validateAiCompletionRules = (rules) => {
  for (const rule of Array.isArray(rules) ? rules : []) {
    const eventType = rule.event_type || DEFAULT_AI_COMPLETION_EVENT;
    if (needsMedia(eventType)) {
      if (!rule.media_id) return `Select a prompt for completion type "${rule.name}"`;
      if (!rule.media_language) return `Select a prompt language for completion type "${rule.name}"`;
    }
    if (needsTransfer(eventType)) {
      if (!rule.transfer_type) return `Select a transfer type for completion type "${rule.name}"`;
      if (!rule.transfer_id) return `Select a transfer destination for completion type "${rule.name}"`;
    }
  }
  return null;
};

const AiAgentCompletionConfig = ({
  selectedAiAgentId,
  onSelectAiAgent,
  rules,
  onRulesChange,
  agents = [],
  callGroups = [],
  ivrs = [],
  // Which IVR field is the transfer target id: DID inbound routing uses flow_id; the
  // IVR Missed-call node stores the IVR's own id.
  ivrIdKey = 'flow_id',
}) => {
  const [aiAgents, setAiAgents] = useState([]);
  const [loadingAiAgents, setLoadingAiAgents] = useState(false);
  const [completionTypes, setCompletionTypes] = useState([]);
  const [loadingCompletionTypes, setLoadingCompletionTypes] = useState(false);
  const [prompts, setPrompts] = useState([]);

  // Seeding reads the latest rules without re-running when they change, so
  // edits (and hydrated values on the edit page) are never clobbered.
  const rulesRef = useRef(rules);
  rulesRef.current = rules;

  const user = getUser();

  // Inbound routing stores the local numeric id (ai_agent_config.id) in
  // numbers.routing_id, but the AI service's HTTP API is keyed on the agent
  // UUID, so both are kept per option. Bots without a local row cannot be
  // routed to and are left out. Only published bots are routable (backend
  // setInboundRouting enforces the same).
  const aiAgentOptions = useMemo(
    () =>
      aiAgents
        .filter((item) => item?.id !== null && item?.id !== undefined && item?.id !== '')
        .filter((item) => !item?.is_delete && String(item?.status || '').toLowerCase() === 'published')
        .map((item) => ({
          id: String(item.id),
          agentUuid: String(item.agent_id ?? ''),
          name: item.name ?? item.agent_name ?? '',
        })),
    [aiAgents],
  );

  const selectedAgentUuid = useMemo(
    () => aiAgentOptions.find((item) => item.id === String(selectedAiAgentId))?.agentUuid || '',
    [aiAgentOptions, selectedAiAgentId],
  );

  useEffect(() => {
    setLoadingAiAgents(true);
    listAgents(user?.id)
      .then((list) => setAiAgents(Array.isArray(list) ? list : []))
      .catch((err) => toast.warning(typeof err === 'string' ? err : 'Failed to load AI agents'))
      .finally(() => setLoadingAiAgents(false));
  }, []);

  useEffect(() => {
    getApiCall(`${API_PROMPT_ACTIVE_LIST}/${user?.id}`)
      .then((resp) => {
        const list = Array.isArray(resp) ? resp : [];
        setPrompts(list.filter(isSelectablePrompt));
      })
      .catch(() => setPrompts([]));
  }, []);

  useEffect(() => {
    if (!selectedAiAgentId) {
      setCompletionTypes([]);
      onRulesChange([]);
      return;
    }
    // Agent list still loading: keep any hydrated rules until the UUID resolves.
    if (!selectedAgentUuid) return;

    let cancelled = false;
    setLoadingCompletionTypes(true);

    getCompletionTypes(selectedAgentUuid)
      .then((resp) => {
        if (cancelled) return;
        const types = toCompletionTypeList(resp);
        setCompletionTypes(types);

        const existing = Array.isArray(rulesRef.current) ? rulesRef.current : [];
        onRulesChange(
          types.map((type) => {
            const saved = existing.find((rule) => rule.name === type.name);
            return {
              name: type.name,
              description: type.description,
              event_type: saved?.event_type || DEFAULT_AI_COMPLETION_EVENT,
              media_id: saved?.media_id ? String(saved.media_id) : '',
              media_language: saved?.media_language || '',
              transfer_type: toTransferType(saved?.transfer_type) || '',
              transfer_id: saved?.transfer_id ? String(saved.transfer_id) : '',
            };
          }),
        );
      })
      .catch((err) => {
        if (cancelled) return;
        setCompletionTypes([]);
        onRulesChange([]);
        toast.warning(typeof err === 'string' ? err : 'Failed to load completion types');
      })
      .finally(() => {
        if (!cancelled) setLoadingCompletionTypes(false);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedAiAgentId, selectedAgentUuid]);

  const transferOptionsFor = (transferType) => {
    if (transferType === AI_TRANSFER_TARGET_TYPES.AGENT) {
      return agents
        .filter((item) => String(item.access).toLowerCase() !== 'team_lead')
        .map((item) => ({ id: String(item.id), name: item.agent_name }));
    }
    if (transferType === AI_TRANSFER_TARGET_TYPES.CALL_GROUP) {
      return callGroups.map((item) => ({ id: String(item.id), name: item.group_name }));
    }
    if (transferType === AI_TRANSFER_TARGET_TYPES.AI) {
      return aiAgentOptions.filter((item) => item.id !== String(selectedAiAgentId));
    }
    if (transferType === AI_TRANSFER_TARGET_TYPES.IVR) {
      return ivrs
        .filter(
          (item) =>
            Number(item.status) === 1 &&
            !item.is_deleted &&
            String(item.ivr_category).toLowerCase() !== 'missed',
        )
        .map((item) => ({ id: String(item[ivrIdKey]), name: item.flow_name }));
    }
    return [];
  };

  const updateRule = (name, patch) => {
    onRulesChange(rules.map((rule) => (rule.name === name ? { ...rule, ...patch } : rule)));
  };

  const handleEventTypeChange = (rule, eventType) => {
    updateRule(rule.name, {
      event_type: eventType,
      media_id: needsMedia(eventType) ? rule.media_id : '',
      media_language: needsMedia(eventType) ? rule.media_language : '',
      transfer_type: needsTransfer(eventType) ? rule.transfer_type : '',
      transfer_id: needsTransfer(eventType) ? rule.transfer_id : '',
    });
  };

  const promptLanguages = (promptId) =>
    approvedLanguageEntries(prompts.find((p) => String(p.id) === String(promptId)));

  return (
    <>
      <div className="row mb-3">
        <div className="col-md-8">
          <label htmlFor="aiAgentDropdown" className="form-label">
            Select AI Agent <span style={{ color: 'red', margin: '0 4px' }}>*</span>
          </label>
          <select
            id="aiAgentDropdown"
            className="form-control"
            value={selectedAiAgentId || ''}
            onChange={(e) => {
              const picked = aiAgentOptions.find((item) => item.id === e.target.value);
              onSelectAiAgent(picked?.id || '', picked?.name || '');
            }}
            disabled={loadingAiAgents}
          >
            <option value="">
              {loadingAiAgents ? 'Loading AI agents…' : 'Select AI Agent'}
            </option>
            {aiAgentOptions.map((item) => (
              <option key={item.id} value={item.id}>
                {item.name}
              </option>
            ))}
          </select>
          {!loadingAiAgents && aiAgentOptions.length === 0 && (
            <small className="text-muted d-block mt-1">
              No published AI agents found. Create and publish one under AI Agent → AI Agent Builder first.
            </small>
          )}
        </div>
      </div>

      {selectedAiAgentId && (
        <div className="row mb-3">
          <div className="col-md-12">
            <label className="form-label">Completion Handling</label>
            <small className="text-muted d-block mb-2">
              Choose what happens to the call when the AI agent reaches each completion
              type. Anything left as Hangup ends the call.
            </small>

            {loadingCompletionTypes && (
              <p className="text-muted">Loading completion types…</p>
            )}

            {!loadingCompletionTypes && completionTypes.length === 0 && (
              <p className="text-muted">
                This AI agent has no completion types configured.
              </p>
            )}

            {!loadingCompletionTypes &&
              rules.map((rule) => {
                const languages = promptLanguages(rule.media_id);
                return (
                  <div
                    key={rule.name}
                    className="mb-3"
                    style={{ border: '1px solid #dee2e6', borderRadius: 8, padding: 12 }}
                  >
                    <div className="row">
                      <div className="col-md-6">
                        <strong>{rule.name}</strong>
                        {rule.description && (
                          <small className="text-muted d-block">{rule.description}</small>
                        )}
                      </div>
                      <div className="col-md-6">
                        <label className="form-label">Action</label>
                        <select
                          className="form-control"
                          value={rule.event_type || DEFAULT_AI_COMPLETION_EVENT}
                          onChange={(e) => handleEventTypeChange(rule, e.target.value)}
                        >
                          {AI_COMPLETION_EVENT_TYPES.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {needsMedia(rule.event_type) && (
                      <div className="row mt-3">
                        <div className="col-md-6">
                          <label className="form-label">
                            Select Prompt <span style={{ color: 'red', margin: '0 4px' }}>*</span>
                          </label>
                          <select
                            className="form-control"
                            value={rule.media_id || ''}
                            onChange={(e) =>
                              updateRule(rule.name, {
                                media_id: e.target.value,
                                media_language: '',
                              })
                            }
                          >
                            <option value="">Select Prompt</option>
                            {prompts.map((prompt) => (
                              <option key={prompt.id} value={prompt.id}>
                                {prompt.prompt_name}
                              </option>
                            ))}
                          </select>
                        </div>
                        {rule.media_id && (
                          <div className="col-md-6">
                            <label className="form-label">
                              Prompt Language{' '}
                              <span style={{ color: 'red', margin: '0 4px' }}>*</span>
                            </label>
                            <select
                              className="form-control"
                              value={rule.media_language || ''}
                              onChange={(e) =>
                                updateRule(rule.name, { media_language: e.target.value })
                              }
                              disabled={languages.length === 0}
                            >
                              <option value="">
                                {languages.length === 0
                                  ? 'No approved language for this prompt'
                                  : 'Select Language'}
                              </option>
                              {languages.map((entry) => (
                                <option key={entry.language} value={entry.language}>
                                  {String(entry.language).toUpperCase()}
                                </option>
                              ))}
                            </select>
                          </div>
                        )}
                      </div>
                    )}

                    {needsTransfer(rule.event_type) && (
                      <div className="row mt-3">
                        <div className="col-md-6">
                          <label className="form-label">
                            Transfer To <span style={{ color: 'red', margin: '0 4px' }}>*</span>
                          </label>
                          <select
                            className="form-control"
                            value={rule.transfer_type || ''}
                            onChange={(e) =>
                              updateRule(rule.name, {
                                transfer_type: e.target.value,
                                transfer_id: '',
                              })
                            }
                          >
                            <option value="">Select Transfer Type</option>
                            {AI_COMPLETION_TRANSFER_TYPES.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        </div>
                        {rule.transfer_type && (
                          <div className="col-md-6">
                            <label className="form-label">
                              Destination <span style={{ color: 'red', margin: '0 4px' }}>*</span>
                            </label>
                            <select
                              className="form-control"
                              value={rule.transfer_id || ''}
                              onChange={(e) =>
                                updateRule(rule.name, { transfer_id: e.target.value })
                              }
                            >
                              <option value="">Select Destination</option>
                              {transferOptionsFor(rule.transfer_type).map((option) => (
                                <option key={option.id} value={option.id}>
                                  {option.name}
                                </option>
                              ))}
                            </select>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
          </div>
        </div>
      )}
    </>
  );
};

export default AiAgentCompletionConfig;
